import { ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ArchiveListingModalActionType from './logic/models/archiveListingModalActionType';
import style from './ArchiveListingModal.module.css';
import ModalNew from '../../organisms/modal/Modal';
import Text from '../../../componentsNew/atoms/typography/Text';
import { ColorEnum as ColorEnumNew, FontSizeEnum, FontWeightEnum } from '../../../componentsNew/enums';
import ColorEnum from '../../../enums/colorEnum';
import ButtonNew from '../../../componentsNew/molecules/button/Button';
import Form from '../../../componentsNew/molecules/form/Form';
import FormError from '../../../componentsNew/molecules/form/FormError';
import { ApprovedHomeseekers, findApprovedHomeseekersById, FindApprovedHomeseekersByIdResponse } from '../../../api/transactional-listing/findApprovedHomeseekersById';
import { Item } from '../../../componentsNew/organisms/select/Select';
import { RadioInput } from '../../../componentsNew/atoms/radio-input';
import SkeletonComponent from '../../../components/SkeletonComponent';
import Label from '../../../componentsNew/atoms/label/Label';
import { Badge } from '../../../componentsNew/molecules/badge';
import UserStarLineIcon from '../../../componentsNew/atoms/icons/UserStarLineIcon';
import { SelectNew } from '../../../componentsNew/organisms/select-new';
import Notifications from '../../../componentsNew/organisms/notifications/Notifications';
import ArchiveListingModalEventManager, { ArchiveListingModalCustomEventType, ArchiveListingModalEventData } from '../../../componentsNew/organisms/modal/ArchiveListingModalEventManager';
import findListingById, { ContactRequestListingResponse } from '../../../api/transactional-listing/findListingById';
import HighlightBlock from '../../../componentsNew/molecules/highlightBlock/HighlightBlock';
import { Link } from 'react-router-dom';
import { useRoutePath } from '../../../componentsNew/utils/useRoutePath';
import archiveListingAndAssignTenant from '../../../api/transactional-listing/archiveListingAndAssignTenant';
import { ErrorTypes } from '../../generics/error-class/ObjectError'

const archiveListingModal = ArchiveListingModalEventManager.create<ArchiveListingModalEventData>('archive-listing-modal');

export interface ArchiveModalProps {
    readonly onButtonClick?: (listingId: string, actionType: ArchiveListingModalActionType) => void;
    readonly onCloseButtonClick?: () => void;
    readonly open?: boolean;
}

type ArchiveListingModalComponent = (props: ArchiveModalProps) => JSX.Element;

function ArchiveListingModal({
    open
}: ArchiveModalProps) {
    const { t } = useTranslation();
    const routePath = useRoutePath();

    const [shouldAssignTenant, setShouldAssignTenant] = useState<boolean>(false);
    const [shouldSendMessagesOnly, setShouldSendMessagesOnly] = useState<boolean>(false);
    const [isBulkAction, setIsBulkAction] = useState<boolean>(false);
    const [refreshListings, setRefreshListings] = useState(null);
    const [transactionalListingIds, setTransactionalListingIds] = useState<string[]>(null);
    const [transactionalListingData, setTransactionalListingData] = useState<ContactRequestListingResponse>(null);
    const [transactionalListingId, setTransactionalListingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [homeseekers, setHomeseekers] = useState<FindApprovedHomeseekersByIdResponse>();
    const [radioValue, setRadioValue] = useState<string | null>('true');
    const [hasTenant, setHasTenant] = useState(false);
    const [tenant, setTenant] = useState<ApprovedHomeseekers>(null);
    const [formData, setFormData] = useState({});
    const [, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    const radioOptions = [
        {
            label:
                <div className={style.radioLabel}>
                    <Text
                        tag='p'
                        color={ColorEnumNew.Gray800}
                    >
                        {t('listings.archive.modal.sendUpdate')}
                    </Text>
                    <Text
                        tag='p'
                        size={FontSizeEnum.Sm}
                        color={ColorEnumNew.Gray500}
                    >
                        {(!isBulkAction) ?
                            t('listings.archive.modal.sendUpdateUnderText')
                            :
                            t('listings.archive.modal.sendBulkUpdateUnderText')
                        }
                    </Text>
                </div>,
            value: 'true'
        },
        {
            label:
                <div>
                    <Text
                        tag='p'
                        color={ColorEnumNew.Gray800}
                    >
                        {t('listings.archive.modal.dontSendUpdate')}
                    </Text>
                    <Text
                        tag='p'
                        size={FontSizeEnum.Sm}
                        color={ColorEnumNew.Gray500}
                    >
                        {t('listings.archive.modal.dontSendUpdateUnderText')}
                    </Text>
                </div>,
            value: 'false'
        },
    ];

    const setArchiveModalData = (e: ArchiveListingModalCustomEventType) => {
        setShouldAssignTenant(e.detail.shouldAssignTenant);
        setTransactionalListingId(e.detail.transactionalListingId);
        setShouldSendMessagesOnly(e.detail.shouldSendMessagesOnly);
        setTransactionalListingIds(e.detail.transactionalListingIds);
        setRefreshListings(e.detail.refreshListings);
        setIsBulkAction(e.detail.isBulkAction);
    }


    useEffect(() => {
        archiveListingModal.addListener(setArchiveModalData);

        return () => {
            archiveListingModal.removeListener(setArchiveModalData);
        }
    }, []);

    useEffect(() => {
        if (transactionalListingId) {
            fetchArchiveModalData();
        }
    }, [transactionalListingId]);

    useEffect(() => {
        if (homeseekers) {
            checkForTenant();
            setLoading(false);
        }
    }, [homeseekers])

    const checkForTenant = () => {
        for (const homeseeker of homeseekers.approvedHomeseekers) {
            if (homeseeker.isTenant) {
                setHasTenant(true);
                setTenant(homeseeker);
                setFormData([]);

                return;
            }
        }

        setTenant(null);
        setHasTenant(false);
    }

    const fetchArchiveModalData = async () => {
        try {
            setLoading(true);
            const response = await findApprovedHomeseekersById(transactionalListingId);
            const listingData = await findListingById(transactionalListingId);

            setTransactionalListingData(listingData);
            setHomeseekers(response);
        } catch (e) {
            Notifications.add({
                message: t('listings.archive.modal.failed'),
                type: 'failed'
            });
        }
    }

    const handleFormChange = (data, isValid, errors) => {
        setFormData(data);
        setIsFormValid(isValid);
        setFormErrors(errors);
    }

    const getListingAddress = (): string => {
        if (!transactionalListingData) {
            return;
        }

        const listing = transactionalListingData.listing;

        return `${listing.street} ${listing.houseNumber}, ${listing.city}`;
    }

    const getAllApprovedHomeseekers = (homeseekers: FindApprovedHomeseekersByIdResponse): Item[] => {
        if (!homeseekers) {
            return [];
        }

        const sortedHomeseekers = homeseekers.approvedHomeseekers.sort((first, second) => {
            const removedPrefix = (n: string) => n.replace(/^[^.]+\.\s+/, '');

            return removedPrefix(first.name).localeCompare(removedPrefix(second.name));
        }).map((hs) => ({
            label: hs.name,
            value: hs.id,
        }));

        return [
            {
                label: (
                    <div data-testid='defaultValue'>
                        {t('listings.archive.modal.notInLeadFlow')}
                    </div>
                ),
                value: '1'
            },
            ...sortedHomeseekers
        ];
    };

    const isHomeseekerValid = () => {
        return (formData['tenant']?.value !== '1' && formData['tenant']?.value);
    }

    const getTenant = (): string => {
        if (formData['tenant'] && (formData['tenant']?.value === '1' || formData['tenant']?.value === '')) {
            return null;
        }

        if (hasTenant) {
            return tenant.id;
        }

        const selectedTenant = homeseekers.approvedHomeseekers.find(hs => hs.id === formData['tenant']?.value);
        if (selectedTenant === undefined) {
            return null;
        }

        return selectedTenant.id;
    }

    const getShouldSendMessages = (): boolean => {
        return (shouldSendMessagesOnly || (formData['sendMessage']?.value === 'true'));
    }

    const getSelectedTenantName = (): string => {
        if (!isHomeseekerValid()) {
            return;
        }

        const selectedTenant = homeseekers.approvedHomeseekers.find(homeseeker => homeseeker.id === formData['tenant']?.value);

        if (selectedTenant === undefined) {
            return;
        }

        return selectedTenant?.name;
    }

    const renderRadio = (): ReactNode => {
        if (shouldSendMessagesOnly) {
            return (
                <Text>
                    {t('listings.archive.modal.sendUpdateUnderTextForEmails')}
                </Text>
            );
        }

        return (
            <>
                <RadioInput
                    name='sendMessage'
                    value={radioValue}
                    options={radioOptions}
                    onChange={setRadioValue}
                    required
                />
                <FormError errors={formErrors['sendMessage']} />
            </>
        )
    }

    const renderTitle = (): ReactNode => {
        if (shouldSendMessagesOnly) {
            return (
                t('listings.archive.modal.updateMessage', {
                    address: getListingAddress()
                })
            );
        }

        if (shouldAssignTenant) {
            return (
                t('listings.archive.modal.selectTenantHeading', {
                    address: getListingAddress()
                })
            );
        }

        if (isBulkAction && transactionalListingIds) {
            return (
                t('listings.archive.modal.bulkTitle', {
                    count: transactionalListingIds.length
                })
            );
        }

        return (
            t('listings.archive.modal.title', {
                address: getListingAddress()
            })
        );
    }

    const renderWarning = (): ReactNode => {
        if (shouldAssignTenant || shouldSendMessagesOnly) {
            return null;
        }

        if (isBulkAction && transactionalListingIds) {
            return (
                <Text>
                    {t('listings.archive.modal.bulkWarning', {
                        count: transactionalListingIds.length
                    })}
                </Text>
            );
        }

        return (
            <Text>
                {t('listings.archive.modal.warning')}
            </Text>
        );
    }

    const getIconName = (): string => {
        if (shouldSendMessagesOnly) {
            return 'ri-mail-line';
        }

        if (shouldAssignTenant) {
            return 'ri-user-star-line';
        }

        return 'ri-archive-line';
    }

    const renderButtonText = (): ReactNode => {
        if (shouldSendMessagesOnly) {
            return t('listings.archive.modal.sendUpdateButton');
        }

        if (shouldAssignTenant) {
            return t('listings.archive.modal.setTenantButton');
        }

        return t('listings.archive.modal.archive');
    }

    const renderFailedNotificationContent = (ex): ReactNode => {
        return (ex.keys.map((key, index) => {
            return (
                <span key={index}>
                    <Trans
                        i18nKey={key}
                        values={{ tenant: getSelectedTenantName(), address: getListingAddress() }}
                        components={{ strong: <strong /> }}
                    />
                </span>
            )
        }))
    }

    const handleSubmit = async () => {
        if (isBulkAction) {
            archiveListingModal.close();

            try {
                await archiveListingAndAssignTenant({
                    homeseekerApplicationId: null,
                    transactionalListingIds: transactionalListingIds.slice(),
                    shouldNotifyHomeseekers: getShouldSendMessages()
                });

                Notifications.add({
                    type: 'success',
                    message: 'Berichten successvol verstuurd'
                });
            } catch (e) {
                Notifications.add({
                    type: 'failed',
                    message: t('api.request.failed')
                });
            }
        }
        try {
            archiveListingModal.close();

            if (formData['sendMessage']?.value === 'true' && !isBulkAction) {
                Notifications.add({
                    type: 'info',
                    heading: t('listings.archive.modal.messagesBeingSent'),
                    message: t('listings.archive.modal.messagesBeingSentSecondary'),
                    sticky: true
                })
            }

            if (isHomeseekerValid()) {
                const tenant = homeseekers.approvedHomeseekers.find((hs) => hs.id === formData['tenant']?.value);

                if (tenant === undefined) {
                    return;
                }

                Notifications.add({
                    type: 'success',
                    heading: t('listings.archive.modal.validation.toast.succeeded', {
                        address: getListingAddress()
                    }),
                    message: <Trans
                        i18nKey="listings.archive.modal.validation.toast.succeededWithTenant"
                        values={{ tenantName: tenant.name }}
                        components={{ strong: <strong /> }}
                    />,
                });
            }

            await archiveListingAndAssignTenant({
                homeseekerApplicationId: getTenant(),
                transactionalListingIds: [transactionalListingId],
                shouldNotifyHomeseekers: getShouldSendMessages()
            });

            if (formData['sendMessage']?.value === 'true' || shouldSendMessagesOnly) {
                Notifications.add({
                    type: 'success',
                    heading: t('listings.archive.modal.messagesSent'),
                    message: <Trans
                        i18nKey="listings.archive.modal.gotMessage"
                        values={{ address: getListingAddress() }}
                        components={{ strong: <strong /> }}
                    />
                });
            } else {
                Notifications.add({
                    type: 'success',
                    message: <Trans
                        i18nKey={t('listings.archive.modal.validation.toast.succeeded')}
                        values={{ address: getListingAddress() }}
                        components={{ strong: <strong /> }}
                    />
                });
            }
        } catch (ex) {
            switch (ex.type) {
                case ErrorTypes.ASSIGN_TENANT_FAILED:
                    Notifications.add({
                        message: (
                            <div className={style.assignTenantFailedContainer}>
                                {renderFailedNotificationContent(ex)}
                            </div>
                        ),
                        heading: t('listings.archive.modal.validation.toast.failedToAssignTenant'),
                        sticky: true,
                        customButton: (
                            <ButtonNew
                                variant='empty'
                                onClick={() => { archiveListingModal.open(transactionalListingId, false, null, refreshListings, true, false) }}
                            >
                                {t('listings.archive.modal.validation.toast.tryAgain')}
                            </ButtonNew>
                        ),
                        type: 'failed'
                    });

                    break;
                case ErrorTypes.SEND_MESSAGE_FAILED:
                    Notifications.add({
                        message: (
                            <Trans
                                i18nKey={ex.keys}
                                values={{ address: getListingAddress() }}
                                components={{ strong: <strong /> }}
                            />
                        ),
                        heading: t('listings.archive.modal.messageNotSentHeading'),
                        sticky: true,
                        customButton: (
                            <ButtonNew
                                variant='empty'
                                onClick={() => { archiveListingModal.open(transactionalListingId, false, null, refreshListings, false, true) }}
                            >
                                {t('listings.archive.modal.validation.toast.tryAgain')}
                            </ButtonNew>
                        ),
                        type: 'failed'
                    });

                    break
                case ErrorTypes.ARCHIVE_FAILED:
                    Notifications.add({
                        message: (
                            <div className={style.assignTenantFailedContainer}>
                                {renderFailedNotificationContent(ex)}
                            </div>
                        ),
                        heading: t('listings.archive.modal.archiveFailed', {
                            address: getListingAddress()
                        }),
                        sticky: true,
                        customButton: (
                            <ButtonNew
                                variant='empty'
                                onClick={() => { archiveListingModal.open(transactionalListingId, false, null, refreshListings, false, false) }}
                            >
                                {t('listings.archive.modal.validation.toast.tryAgain')}
                            </ButtonNew>
                        ),
                        type: 'failed'
                    });

                    break;
            }
        } finally {
            handleArchiveModalClose();
        }
    }

    const openSettingsPage = () => {
        window.open(routePath('SettingsEmailTemplates'))
    }

    const skeletonComponents = (): ReactNode => {
        return (
            <div>
                <div className={style.modal}>
                    <SkeletonComponent width={300} height={20} />
                    <SkeletonComponent width={600} height={40} />
                    <div className={style.selectContainer}>
                        <SkeletonComponent width={150} height={20} />
                        <SkeletonComponent width={280} height={40} />
                    </div>
                    <SkeletonComponent width={320} height={20} />
                    <SkeletonComponent width={600} height={45} />
                    <SkeletonComponent width={300} height={45} />
                </div>
                <div className={style.buttons}>
                    <SkeletonComponent width={100} height={40} />
                    <SkeletonComponent width={100} height={40} />
                </div>
            </div>
        );
    }

    const handleArchiveModalClose = () => {
        const formDataClone = formData;
        formDataClone['tenant'] = {};

        setFormData(formDataClone);
        setRadioValue('true');

        archiveListingModal.close();
    }

    return (
        <div>
            <ModalNew
                isOpen={open}
                flowyColor={ColorEnum.Primary100}
                icon={getIconName()}
                onCloseButtonClick={handleArchiveModalClose}
                iconColor={ColorEnum.Primary600}
                iconBackgroundColor={ColorEnum.Primary100}
                canOverflow={true}
                isNonScrollable={true}
                manager={archiveListingModal}
            >
                {(loading) ?
                    skeletonComponents()
                    :
                    <Form className={style.archiveForm} onChange={handleFormChange} onSubmit={handleSubmit}>
                        <div className={style.modal}>
                            <Text
                                size={FontSizeEnum.Lg}
                                weight={FontWeightEnum.Semibold}
                            >
                                {renderTitle()}
                            </Text>
                            {renderWarning()}
                            {!isBulkAction &&
                                <>
                                    <div className={style.selectContainer}>
                                        {!shouldSendMessagesOnly &&
                                            <Label required>{t('listings.archive.modal.tenant')}</Label>
                                        }
                                        {hasTenant ?
                                            <div className={style.tenantBadgeContainer}>
                                                <Badge
                                                    before={<UserStarLineIcon />}
                                                    className={style.tenantBadge}
                                                    label={t('listings.archive.modal.tenantBadge')}
                                                />
                                                <Text
                                                    size={FontSizeEnum.Md}
                                                    weight={FontWeightEnum.Regular}
                                                >
                                                    {` : ${tenant.name}`}
                                                </Text>
                                            </div>
                                            :
                                            <SelectNew
                                                options={getAllApprovedHomeseekers(homeseekers)}
                                                placeholder={t('listings.archive.modal.selectTenant')}
                                                selected={formData['tenant']?.value}
                                                hasInputField
                                                name='tenant'
                                                required
                                            />
                                        }
                                        <FormError errors={formErrors['tenant']} />
                                    </div>
                                </>
                            }
                            <Text
                                size={FontSizeEnum.Md}
                                weight={FontWeightEnum.Regular}
                            >
                                {t('listings.archive.modal.sendMessage')}
                            </Text>
                            {renderRadio()}
                            <HighlightBlock
                                type='none'
                            >
                                {t('listings.archive.modal.highlightMessage')}
                                <Link
                                    to={''}
                                    onClick={openSettingsPage}
                                    className={style.emailLink}
                                >
                                    {t('listings.archive.modal.emailTemplates')}
                                </Link>
                            </HighlightBlock>
                        </div>
                        <div className={style.buttons}>
                            <ButtonNew variant='secondary-gray' onClick={handleArchiveModalClose}>
                                {t('listings.archive.modal.cancel')}
                            </ButtonNew>
                            <ButtonNew type='submit' className={style.archiveSubmitButton}>
                                {renderButtonText()}
                            </ButtonNew>
                        </div>
                    </Form>
                }
            </ModalNew>
        </div>
    )
}

Object.assign(ArchiveListingModal, archiveListingModal);

export default (ArchiveListingModal as ArchiveListingModalComponent & ArchiveListingModalEventManager<ArchiveListingModalEventData>);
